import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  AccessTime,
  AddCircleOutlineRounded,
  ArrowBackRounded,
  AssignmentIndOutlined,
  CheckCircle,
  ChatBubble,
  ChevronRight,
  DirectionsCar,
  EditNoteRounded,
  ExpandLess,
  ExpandMore,
  GpsFixed,
  LocationOn,
  MyLocation,
  Navigation,
  Person,
  ReportProblemOutlined,
  Route,
  ShieldOutlined,
  WarningAmberRounded,
  WheelchairPickup,
} from '@mui/icons-material'
import moment from 'moment'
import { UserContext } from '../../../App'
import { SiteModeContext } from '../../../components/mainFrame/MainFrame'
import API from '../../../services/API'
import { getJourneyWorkflow, isRiskAssessmentJourney } from '../journeyWorkflow'
import JourneyTimesStrip from '../components/JourneyTimesStrip'
import PTSDutyChat from '../components/PTSDutyChat'
import { getArrivalProximityCheck } from '../proximity'
import ArrivalProximityDialog from '../components/ArrivalProximityDialog'
import CrewDutiesList from './CrewDutiesList'
import CrewJourneyDetails from './CrewJourneyDetails'
import CrewJobDialog from './CrewJobDialog'
import { describeOutstanding } from './crewJobForm'
import RiskAssessmentDialog, {
  RiskChip,
} from '../Patients/RiskAssessmentDialog'

const CLOSED_STATUSES = ['Completed', 'Cancelled', 'No Show', 'Aborted']

// Keep in sync with DELAY_REASON_CODES in
// server/src/models/PatientTransport/Journey.js
const DELAY_REASON_CODES = [
  'Traffic',
  'Ward Delay',
  'Patient Not Ready',
  'Equipment Issue',
  'Vehicle Fault',
  'Crew Break',
  'Awaiting Handover',
  'Prior Job Overran',
  'Other',
]

const getJourneyTime = (journey) =>
  journey?.pickup?.when?.exactTime ||
  journey?.pickup?.when?.patientReadyFrom ||
  journey?.dropoff?.when?.exactTime ||
  journey?.dropoff?.when?.targetArrivalTime

const getAddressLine = (location) =>
  [
    location?.name,
    location?.wardName,
    location?.addressLine1,
    location?.town || location?.city,
    location?.postCode,
  ]
    .filter(Boolean)
    .join(', ')

const getStopCoords = (location) =>
  Array.isArray(location?.location?.coordinates) &&
  location.location.coordinates.length === 2
    ? location.location.coordinates
    : null

const getNavigationTarget = (journey, workflow) => {
  if (!journey || !workflow?.nextStatus) return null

  const target =
    workflow?.actionLabel === 'Arrived at pickup'
      ? {
          label: 'pickup',
          stop: journey.pickup,
        }
      : workflow?.actionLabel === 'Arrived at patient home'
        ? {
            label: 'patient home',
            stop: journey.pickup,
          }
        : workflow?.actionLabel === 'Arrived at destination'
          ? {
              label: 'destination',
              stop: journey.dropoff,
            }
          : null

  // A crew-added job whose destination isn't known yet is parked on the
  // pickup's coordinates — never send the sat nav there
  if (!target?.stop || target.stop.pending) return null

  const coords = getStopCoords(target.stop)
  const address = getAddressLine(target.stop)
  const query = coords ? `${coords[1]},${coords[0]}` : address

  if (!query) return null

  return {
    ...target,
    coords,
    query,
    encodedQuery: encodeURIComponent(query),
  }
}

const getNavigationOptions = (target) => {
  if (!target) return []

  const appleQuery = target.coords
    ? `${target.coords[1]},${target.coords[0]}`
    : target.query
  const googleQuery = target.encodedQuery
  const wazeQuery = target.encodedQuery

  return [
    {
      label: 'Device default',
      url: target.coords
        ? `geo:${target.coords[1]},${target.coords[0]}?q=${target.coords[1]},${target.coords[0]}`
        : `https://www.google.com/maps/search/?api=1&query=${googleQuery}`,
    },
    {
      label: 'Apple Maps',
      url: `https://maps.apple.com/?daddr=${encodeURIComponent(
        appleQuery,
      )}&dirflg=d`,
    },
    {
      label: 'Google Maps',
      url: `https://www.google.com/maps/dir/?api=1&destination=${googleQuery}&travelmode=driving`,
    },
    {
      label: 'Waze',
      url: `https://waze.com/ul?q=${wazeQuery}&navigate=yes`,
    },
  ]
}

const getPatient = (journey) => journey?.booking?.patient || journey?.patient

const getPatientName = (journey) => {
  const patient = getPatient(journey)
  return (
    [patient?.demographics?.firstName, patient?.demographics?.lastName]
      .filter(Boolean)
      .join(' ') || 'Unknown patient'
  )
}

const formatTime = (value) => (value ? moment(value).format('HH:mm') : '--:--')

const getRiskFlags = (journey) => {
  const flags = getPatient(journey)?.riskFlags
  if (Array.isArray(flags)) return flags.filter(Boolean).map(String)
  if (flags && typeof flags === 'object') {
    return Object.entries(flags)
      .filter(([, value]) => value === true)
      .map(([key]) => key.replace(/([A-Z])/g, ' $1').trim())
  }
  return []
}

const STATUS_TONES = {
  Completed: { bg: '#DCFCE7', fg: '#166534' },
  Cancelled: { bg: '#FEE2E2', fg: '#991B1B' },
  'No Show': { bg: '#FEE2E2', fg: '#991B1B' },
  Aborted: { bg: '#FEE2E2', fg: '#991B1B' },
  Planned: { bg: '#FEF3C7', fg: '#92400E' },
  Assigned: { bg: '#FEF3C7', fg: '#92400E' },
}
const getStatusTone = (status) =>
  STATUS_TONES[status] || { bg: '#E0F2FE', fg: '#075985' }

// Diffs one dashboard poll against the previous so control-room changes
// surface as explicit notices instead of the screen silently re-rendering.
const diffJourneys = (previousJourneys, nextJourneys) => {
  if (!previousJourneys) return []
  const notices = []
  const previousById = new Map(previousJourneys.map((j) => [String(j._id), j]))
  const nextIds = new Set(nextJourneys.map((j) => String(j._id)))

  nextJourneys.forEach((journey) => {
    const before = previousById.get(String(journey._id))
    if (!before) {
      if (!CLOSED_STATUSES.includes(journey.status)) {
        notices.push(
          `New journey added: ${getPatientName(journey)} at ${formatTime(getJourneyTime(journey))}`,
        )
      }
      return
    }
    if (
      before.status !== journey.status &&
      ['Cancelled', 'No Show', 'Aborted'].includes(journey.status)
    ) {
      notices.push(
        `Journey ${journey.status.toLowerCase()}: ${getPatientName(journey)}`,
      )
    }
    const beforeTime = getJourneyTime(before)
    const afterTime = getJourneyTime(journey)
    if (
      beforeTime &&
      afterTime &&
      !moment(beforeTime).isSame(moment(afterTime)) &&
      !CLOSED_STATUSES.includes(journey.status)
    ) {
      notices.push(
        `Time changed for ${getPatientName(journey)}: ${formatTime(beforeTime)} → ${formatTime(afterTime)}`,
      )
    }
  })

  previousJourneys.forEach((journey) => {
    if (!nextIds.has(String(journey._id))) {
      notices.push(`Journey removed from your duty: ${getPatientName(journey)}`)
    }
  })

  // The list is in control's running order — say so when they change it
  // (reordered, or jobs grouped into / split out of a shared run)
  const openKey = (journeys) =>
    journeys
      .filter(
        (j) =>
          !CLOSED_STATUSES.includes(j.status) &&
          previousById.has(String(j._id)) &&
          nextIds.has(String(j._id)),
      )
      // A "|" marks the start of a new run, so a route slotted in elsewhere
      // (shifting every routeIndex) doesn't count as a change
      .map((j, index, list) =>
        index && list[index - 1].routeIndex !== j.routeIndex
          ? `|${j._id}`
          : String(j._id),
      )
      .join(',')
  const orderBefore = openKey(previousJourneys)
  const orderAfter = openKey(nextJourneys)
  if (orderBefore && orderBefore !== orderAfter) {
    notices.push('Control changed the running order of your jobs')
  }

  return notices
}

const playAlertTone = (() => {
  let audioContext = null
  return () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext
      if (!AudioContext) return
      audioContext = audioContext || new AudioContext()
      if (audioContext.state === 'suspended')
        audioContext.resume().catch(() => {})

      const now = audioContext.currentTime
      const gain = audioContext.createGain()
      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(0.09, now + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42)
      gain.connect(audioContext.destination)
      ;[880, 1175].forEach((frequency, index) => {
        const oscillator = audioContext.createOscillator()
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(frequency, now + index * 0.13)
        oscillator.connect(gain)
        oscillator.start(now + index * 0.13)
        oscillator.stop(now + index * 0.13 + 0.18)
      })
    } catch (e) {
      console.warn('Unable to play PTS crew alert tone', e)
    }
  }
})()

// Four metrics don't fit one row on a 390px phone without truncating the
// shift times, so below `sm` they wrap into a 2x2 grid.
const Metric = ({ label, value, icon, grow = 1 }) => (
  <Box
    sx={{
      flex: { xs: '1 1 40%', sm: `${grow} 1 0%` },
      minWidth: 0,
      p: 1,
      borderRadius: 1,
      bgcolor: '#F8FAFC',
      border: '1px solid #E2E8F0',
    }}
  >
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ color: '#0F766E', display: 'flex' }}>{icon}</Box>
      <Box minWidth={0}>
        <Typography
          sx={{ fontSize: '0.68rem', color: '#64748B', fontWeight: 800 }}
        >
          {label}
        </Typography>
        <Typography
          noWrap
          sx={{ fontSize: '0.92rem', color: '#0F172A', fontWeight: 900 }}
        >
          {value}
        </Typography>
      </Box>
    </Stack>
  </Box>
)

// Dark top bar shared by every state of the crew screen (loading, no duty,
// live duty). This screen renders without the main sidebar/header, so the
// back button here is the only way out — it must always be present.
const CrewTopBar = ({ firstName, onBack, right, children }) => (
  <Box
    sx={{
      px: 2,
      pt: 2,
      pb: 2,
      bgcolor: '#0F172A',
      color: '#fff',
    }}
  >
    <Stack spacing={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
      >
        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
          <IconButton
            onClick={onBack}
            aria-label="Back to AmbuNet"
            sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.08)' }}
            size="small"
          >
            <ArrowBackRounded fontSize="small" />
          </IconButton>
          <Box minWidth={0}>
            <Typography sx={{ fontSize: '0.78rem', color: 'white' }}>
              Patient Transport
            </Typography>
            <Typography
              noWrap
              sx={{ fontSize: '1.55rem', fontWeight: 950, color: 'white' }}
            >
              Hi {firstName}
            </Typography>
          </Box>
        </Stack>
        {right}
      </Stack>
      {children}
    </Stack>
  </Box>
)

const StopRow = ({ label, location, time, accent }) => (
  <Stack direction="row" spacing={1.25} alignItems="flex-start">
    <Box
      sx={{
        width: 42,
        minWidth: 42,
        height: 42,
        borderRadius: 1,
        bgcolor: accent,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <LocationOn fontSize="small" />
    </Box>
    <Box minWidth={0} flex={1}>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography sx={{ color: '#0F172A', fontWeight: 900 }}>
          {label}
        </Typography>
        <Typography sx={{ color: accent, fontWeight: 900 }}>
          {formatTime(time)}
        </Typography>
      </Stack>
      <Typography sx={{ color: '#334155', fontWeight: 800 }}>
        {location?.name || label}
      </Typography>
      <Typography sx={{ color: '#64748B', fontSize: '0.82rem' }}>
        {getAddressLine(location)}
      </Typography>
    </Box>
  </Stack>
)

const PTSCrewView = () => {
  const { user } = useContext(UserContext)
  const { setSiteMode } = useContext(SiteModeContext)
  const navigate = useNavigate()
  const [dashboard, setDashboard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [locationState, setLocationState] = useState('idle')
  const [chatOpen, setChatOpen] = useState(false)
  const [satNavAnchorEl, setSatNavAnchorEl] = useState(null)
  const [arrivalPrompt, setArrivalPrompt] = useState(null)
  const [workflowActionLoading, setWorkflowActionLoading] = useState(null)
  const [notices, setNotices] = useState([])
  const [journeyListOpen, setJourneyListOpen] = useState(true)
  // Held by id so the open details view follows the 3s dashboard poll
  const [detailJourneyID, setDetailJourneyID] = useState(null)
  const [delayDialogOpen, setDelayDialogOpen] = useState(false)
  const [delayReasonCode, setDelayReasonCode] = useState('')
  const [delayReasonDraft, setDelayReasonDraft] = useState('')
  const [delaySubmitting, setDelaySubmitting] = useState(false)
  // Self-dispatched duties: { mode: 'create' } or
  // { mode: 'edit', journeyID, notice } while the job dialog is open
  const [jobDialog, setJobDialog] = useState(null)
  const lastLocationSentAtRef = useRef(0)
  const currentLocationRef = useRef(null)
  const currentLocationCapturedAtRef = useRef(0)
  const previousJourneysRef = useRef(null)
  const consecutiveFailuresRef = useRef(0)
  const readControlChatCountsRef = useRef(
    JSON.parse(localStorage.getItem('ptsControlChatReadCounts') || '{}'),
  )

  useEffect(() => {
    setSiteMode('PTSCrew')
    return () => setSiteMode('normal')
  }, [setSiteMode])

  const withUnreadControlChat = useCallback((data) => {
    const dutyId = data?.duty?._id
    const readCount = dutyId
      ? readControlChatCountsRef.current?.[dutyId] || 0
      : 0
    const controlCount = data?.chatAttention?.controlChatMessageCount || 0
    const awaitingReply =
      data?.chatAttention?.controlChatMessagesSinceLastCrewCount || 0

    return {
      ...data,
      chatAttention: {
        ...(data?.chatAttention || {}),
        controlChatMessagesAwaitingReply: awaitingReply,
        unreadControlChatCount: controlCount > readCount ? awaitingReply : 0,
      },
    }
  }, [])

  const loadDashboard = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true)
      try {
        const res = await API.get(`/pts/crew/dashboard?_=${Date.now()}`)
        if (res.status === 200) {
          const noticeTexts = diffJourneys(
            previousJourneysRef.current,
            res.data?.journeys || [],
          )
          previousJourneysRef.current = res.data?.journeys || []

          if (noticeTexts.length) {
            playAlertTone()
            setNotices((prev) => [
              ...prev,
              ...noticeTexts.map((text) => ({
                id: `${Date.now()}-${Math.random()}`,
                text,
              })),
            ])
          }

          setDashboard(withUnreadControlChat(res.data))
          setError(null)
          consecutiveFailuresRef.current = 0
        }
      } catch (err) {
        console.error(err)
        consecutiveFailuresRef.current += 1
        // Only surface an error after repeated consecutive failures — a single
        // missed poll is routine on patchy cellular and shouldn't alarm with a
        // banner over otherwise-fine stale data.
        if (!silent || consecutiveFailuresRef.current >= 3) {
          setError('Unable to load your PTS duty. Retrying...')
        }
      } finally {
        if (!silent) setLoading(false)
      }
    },
    [withUnreadControlChat],
  )

  useEffect(() => {
    loadDashboard()
    const interval = setInterval(() => loadDashboard(true), 3000)

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        loadDashboard(true)
      }
    }

    window.addEventListener('focus', refreshWhenVisible)
    document.addEventListener('visibilitychange', refreshWhenVisible)

    return () => {
      clearInterval(interval)
      window.removeEventListener('focus', refreshWhenVisible)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [loadDashboard])

  const duty = dashboard?.duty
  const journey = dashboard?.currentJourney
  const nextJourney = dashboard?.nextJourney
  const patient = getPatient(journey)
  const workflow = getJourneyWorkflow(journey?.status, journey)
  const navigationTarget = getNavigationTarget(journey, workflow)
  // Patients on the same run who are at the same stage: the next step is
  // logged for all of them at once unless the crew untick it
  const runCompanions = (journey?.runCompanions || [])
    .map((id) =>
      (dashboard?.journeys || []).find((j) => String(j._id) === String(id)),
    )
    .filter(Boolean)
  const [stepWithRun, setStepWithRun] = useState(true)
  const stepKey = `${journey?._id || ''}:${journey?.status || ''}`
  useEffect(() => {
    setStepWithRun(true)
  }, [stepKey])
  const navigationOptions = useMemo(
    () => getNavigationOptions(navigationTarget),
    [navigationTarget],
  )

  // On-scene risk assessment for 'Risk Assessment' home-visit journeys
  const [riskAssessmentOpen, setRiskAssessmentOpen] = useState(false)
  const [journeyAssessments, setJourneyAssessments] = useState([])
  const [savingAssessment, setSavingAssessment] = useState(false)

  const loadJourneyAssessments = useCallback(async () => {
    if (!journey?._id || !isRiskAssessmentJourney(journey)) {
      setJourneyAssessments([])
      return
    }
    try {
      const res = await API.get(
        `/pts/crew/journeys/${journey._id}/risk-assessments`,
      )
      if (res.status === 200) setJourneyAssessments(res.data || [])
    } catch (err) {
      console.error(err)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journey?._id, journey?.journeyType])

  useEffect(() => {
    loadJourneyAssessments()
  }, [loadJourneyAssessments])

  const crewAssessment = useMemo(
    () =>
      (journeyAssessments || []).find(
        (assessment) =>
          String(
            assessment.completedBy?._id || assessment.completedBy || '',
          ) === String(user?._id || ''),
      ) || null,
    [journeyAssessments, user?._id],
  )
  const latestAssessment = journeyAssessments?.[0] || null

  const saveJourneyAssessment = async (assessment) => {
    if (!journey?._id) return
    setSavingAssessment(true)
    try {
      const res = crewAssessment?._id
        ? await API.put(
            `/pts/crew/journeys/${journey._id}/risk-assessments/${crewAssessment._id}`,
            assessment,
          )
        : await API.post(
            `/pts/crew/journeys/${journey._id}/risk-assessments`,
            assessment,
          )
      if ([200, 201].includes(res.status)) {
        setRiskAssessmentOpen(false)
        await loadJourneyAssessments()
      } else {
        setError(res.data?.error || 'Unable to save the risk assessment.')
      }
    } catch (err) {
      console.error(err)
      setError('Unable to save the risk assessment.')
    } finally {
      setSavingAssessment(false)
    }
  }
  const detailJourney = useMemo(
    () =>
      detailJourneyID
        ? (dashboard?.journeys || []).find(
            (j) => String(j._id) === String(detailJourneyID),
          ) || null
        : null,
    [dashboard?.journeys, detailJourneyID],
  )
  const jobDialogJourney = useMemo(
    () =>
      jobDialog?.journeyID
        ? (dashboard?.journeys || []).find(
            (j) => String(j._id) === String(jobDialog.journeyID),
          ) || null
        : null,
    [dashboard?.journeys, jobDialog?.journeyID],
  )
  const crewJobs = dashboard?.crewJobs
  const jobOutstanding = journey?.crewJob?.outstanding || []

  const firstName =
    user?.FirstName ||
    user?.firstName ||
    user?.employee?.userAccount?.firstName ||
    'Crew'

  const openJourneys = useMemo(
    () =>
      (dashboard?.journeys || []).filter(
        (j) => !CLOSED_STATUSES.includes(j?.status),
      ),
    [dashboard?.journeys],
  )

  const completedJourneys =
    (dashboard?.journeys?.length || 0) - openJourneys.length
  const progress =
    dashboard?.journeys?.length > 0
      ? Math.round((completedJourneys / dashboard.journeys.length) * 100)
      : 100

  // Journeys being worked concurrently (e.g. two discharges collected on the
  // same run) — more than one at a time is legitimate
  const activeJourneysCount = useMemo(
    () =>
      (dashboard?.journeys || []).filter(
        (j) =>
          !CLOSED_STATUSES.includes(j?.status) &&
          !['Planned', 'Assigned'].includes(j?.status),
      ).length,
    [dashboard?.journeys],
  )

  const controlMessagesAwaitingReply =
    dashboard?.chatAttention?.controlChatMessagesAwaitingReply || 0
  const unreadControlChatCount =
    dashboard?.chatAttention?.unreadControlChatCount || 0

  const markControlChatRead = () => {
    if (!duty?._id) return
    const nextReadCounts = {
      ...(readControlChatCountsRef.current || {}),
      [duty._id]: dashboard?.chatAttention?.controlChatMessageCount || 0,
    }
    readControlChatCountsRef.current = nextReadCounts
    localStorage.setItem(
      'ptsControlChatReadCounts',
      JSON.stringify(nextReadCounts),
    )
    setDashboard((currentDashboard) =>
      currentDashboard
        ? {
            ...currentDashboard,
            chatAttention: {
              ...(currentDashboard.chatAttention || {}),
              unreadControlChatCount: 0,
            },
          }
        : currentDashboard,
    )
  }

  // Badges the browser tab title so a change is noticeable even if the tab
  // isn't focused (a locked/backgrounded tab can't otherwise get attention).
  useEffect(() => {
    document.title = notices.length
      ? `(${notices.length}) PTS Duty — AmbuNet`
      : 'PTS Duty — AmbuNet'

    return () => {
      document.title = 'AmbuNet'
    }
  }, [notices.length])

  const dismissNotice = (id) => {
    setNotices((prev) => prev.filter((notice) => notice.id !== id))
  }

  useEffect(() => {
    if (
      !dashboard?.locationTrackingEnabled ||
      !duty?._id ||
      !navigator.geolocation
    ) {
      setLocationState('idle')
      return undefined
    }

    setLocationState('starting')

    const sendLocation = async (position) => {
      const now = Date.now()
      currentLocationRef.current = [
        position.coords.longitude,
        position.coords.latitude,
      ]
      currentLocationCapturedAtRef.current = now
      if (now - lastLocationSentAtRef.current < 25000) return
      lastLocationSentAtRef.current = now

      try {
        await API.post('/pts/crew/location', {
          dutyID: duty._id,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        })
        setLocationState('live')
      } catch (err) {
        console.error(err)
        setLocationState('error')
      }
    }

    const watchId = navigator.geolocation.watchPosition(
      sendLocation,
      () => setLocationState('blocked'),
      {
        enableHighAccuracy: true,
        maximumAge: 15000,
        timeout: 20000,
      },
    )

    return () => {
      navigator.geolocation.clearWatch(watchId)
      setLocationState('idle')
    }
  }, [dashboard?.locationTrackingEnabled, duty?._id])

  const getFreshLocationCoords = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null)
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = [position.coords.longitude, position.coords.latitude]
          currentLocationRef.current = coords
          currentLocationCapturedAtRef.current = Date.now()
          resolve(coords)
        },
        () => resolve(null),
        {
          enableHighAccuracy: false,
          maximumAge: 30000,
          timeout: 3000,
        },
      )
    })

  const getRecentLocationCoords = () => {
    if (
      currentLocationRef.current &&
      Date.now() - currentLocationCapturedAtRef.current < 60000
    ) {
      return currentLocationRef.current
    }

    return null
  }

  const saveJourneyStatus = async (
    status,
    manageLoading = true,
    locationSnapshot = null,
  ) => {
    const journeyID = dashboard?.currentJourney?._id
    if (!journeyID || !status) return
    if (manageLoading) setWorkflowActionLoading('updating')
    try {
      // API never rejects on 4xx/5xx — check the status
      const res = await API.put(`/pts/journeys/${journeyID}/status`, {
        status,
        location: locationSnapshot,
        applyToRun: stepWithRun && runCompanions.length > 0,
      })
      if (res.status === 409 && res.data?.errorCode === 'crewJobDetailsOutstanding') {
        setJobDialog({ mode: 'edit', journeyID, notice: res.data.error })
      } else if (res.status !== 200) {
        setError(res.data?.error || 'Unable to update journey workflow.')
      } else if (res.data?.runUpdate?.skipped?.length) {
        // The step went through for this patient but not everyone with them
        setError(
          res.data.runUpdate.skipped
            .map((skip) => {
              const other = (dashboard?.journeys || []).find(
                (j) => String(j._id) === String(skip.journeyID),
              )
              return `${other ? getPatientName(other) : 'Another patient'}: ${skip.error}`
            })
            .join(' '),
        )
      }
      await loadDashboard(true)
    } catch (err) {
      console.error(err)
      setError('Unable to update journey workflow.')
    } finally {
      if (manageLoading) setWorkflowActionLoading(null)
    }
  }

  const submitDelayReason = async () => {
    const journeyID = dashboard?.currentJourney?._id
    const currentStatus = dashboard?.currentJourney?.status
    if (!journeyID || !currentStatus || !delayReasonCode) return
    setDelaySubmitting(true)
    try {
      await API.put(`/pts/journeys/${journeyID}/status`, {
        status: currentStatus,
        delayReasonCode,
        ...(delayReasonDraft.trim()
          ? { delayReason: delayReasonDraft.trim() }
          : {}),
      })
      setDelayDialogOpen(false)
      setDelayReasonCode('')
      setDelayReasonDraft('')
      await loadDashboard(true)
    } catch (err) {
      console.error(err)
      setError('Unable to report the delay. Please try again.')
    } finally {
      setDelaySubmitting(false)
    }
  }

  const updateJourneyStatus = async (status) => {
    const journeyID = dashboard?.currentJourney?._id
    if (!journeyID || !status || workflowActionLoading) return

    // A job the crew added themselves can start with gaps, but the
    // destination is needed to set off with the patient and everything else
    // to complete it — ask for it here rather than letting the step fail
    const currentJob = dashboard?.currentJourney
    if (currentJob?.crewJob) {
      if (status === 'Transporting' && currentJob.dropoff?.pending) {
        setJobDialog({
          mode: 'edit',
          journeyID,
          notice: 'Where are you taking the patient? Add the destination to set off.',
        })
        return
      }
      if (status === 'Completed' && currentJob.crewJob.outstanding?.length) {
        setJobDialog({
          mode: 'edit',
          journeyID,
          notice: `Add ${describeOutstanding(currentJob.crewJob.outstanding)} before completing this journey.`,
        })
        return
      }
    }
    const arrivalTarget =
      workflow?.actionLabel === 'Arrived at pickup' ||
      workflow?.actionLabel === 'Arrived at patient home'
        ? 'pickup'
        : workflow?.actionLabel === 'Arrived at destination'
          ? 'dropoff'
          : null
    setWorkflowActionLoading(arrivalTarget ? 'checkingLocation' : 'updating')

    try {
      const recentCoords = getRecentLocationCoords()
      let locationSource = recentCoords ? 'recentBrowserLocation' : null
      const currentCoords = arrivalTarget
        ? recentCoords ||
          (await getFreshLocationCoords()) ||
          currentLocationRef.current ||
          duty?.vehicle?.location?.coordinates
        : currentLocationRef.current || duty?.vehicle?.location?.coordinates
      if (!locationSource && currentCoords) {
        locationSource =
          currentCoords === duty?.vehicle?.location?.coordinates
            ? 'vehicleLastKnownLocation'
            : 'browserLocation'
      }
      const locationSnapshot = currentCoords
        ? {
            coordinates: currentCoords,
            source: locationSource,
            capturedAt: new Date().toISOString(),
          }
        : null

      const proximityCheck = getArrivalProximityCheck({
        status,
        journey: dashboard?.currentJourney,
        currentCoords,
        targetType: arrivalTarget,
      })

      if (proximityCheck) {
        setArrivalPrompt({ check: proximityCheck, status, locationSnapshot })
        return
      }

      await saveJourneyStatus(status, false, locationSnapshot)
    } finally {
      setWorkflowActionLoading(null)
    }
  }

  const goBack = () => {
    setSiteMode('normal')
    navigate('/')
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#EEF4F3' }}>
        <CrewTopBar firstName={firstName} onBack={goBack} />
        <Box
          minHeight="50vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Box>
      </Box>
    )
  }

  if (!duty) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#EEF4F3' }}>
        <CrewTopBar firstName={firstName} onBack={goBack} />
        <Stack spacing={1} sx={{ p: 1.5 }}>
          <Alert severity="info">
            No active PTS duty is assigned to you right now.
          </Alert>
          <CrewDutiesList />
          <Button
            variant="outlined"
            startIcon={<ArrowBackRounded />}
            onClick={goBack}
            sx={{ alignSelf: 'flex-start', mt: 1, fontWeight: 700 }}
          >
            Back to AmbuNet
          </Button>
        </Stack>
      </Box>
    )
  }

  const workflowButtonLabel =
    workflowActionLoading === 'checkingLocation'
      ? 'Checking location...'
      : workflowActionLoading === 'updating'
        ? 'Updating...'
        : workflow.actionLabel || 'Journey complete'

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pb: 12,
        bgcolor: '#EEF4F3',
      }}
    >
      <CrewTopBar
        firstName={firstName}
        onBack={goBack}
        right={
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              markControlChatRead()
              setChatOpen(true)
            }}
            sx={{
              minWidth: 46,
              width: 46,
              height: 46,
              borderRadius: 1,
              position: 'relative',
              animation:
                unreadControlChatCount > 0
                  ? 'ptsCrewChatPulse 1.1s ease-in-out infinite'
                  : 'none',
              '@keyframes ptsCrewChatPulse': {
                '0%, 100%': {
                  transform: 'scale(1)',
                  boxShadow: '0 0 0 0 rgba(252,211,77,0.58)',
                },
                '50%': {
                  transform: 'scale(1.06)',
                  boxShadow: '0 0 0 7px rgba(252,211,77,0)',
                },
              },
            }}
          >
            <ChatBubble />
            {controlMessagesAwaitingReply > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  minWidth: 20,
                  height: 20,
                  px: 0.45,
                  borderRadius: 999,
                  bgcolor: unreadControlChatCount > 0 ? '#F59E0B' : '#2563EB',
                  color: '#fff',
                  border: '2px solid #0F172A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.72rem',
                  fontWeight: 950,
                  lineHeight: 1,
                }}
              >
                {controlMessagesAwaitingReply}
              </Box>
            )}
          </Button>
        }
      >
        <Paper
          elevation={0}
          sx={{
            p: 1.25,
            borderRadius: 1,
            bgcolor: '#FFFFFF',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <Stack spacing={1.25}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Box minWidth={0}>
                <Typography
                  sx={{
                    color: '#64748B',
                    fontSize: '0.72rem',
                    fontWeight: 800,
                  }}
                >
                  RESOURCE
                </Typography>
                <Typography
                  sx={{
                    color: '#0F172A',
                    fontSize: '1.35rem',
                    fontWeight: 950,
                  }}
                >
                  {duty?.vehicle?.callSign || 'No vehicle'}
                </Typography>
              </Box>
              <Chip
                icon={<GpsFixed />}
                label={
                  locationState === 'live'
                    ? 'Location live'
                    : locationState === 'blocked'
                      ? 'Location blocked'
                      : locationState === 'error'
                        ? 'Location delayed'
                        : locationState === 'starting'
                          ? 'Location starting'
                          : 'Location off'
                }
                size="small"
                sx={{
                  borderRadius: 1,
                  fontWeight: 800,
                  bgcolor: locationState === 'live' ? '#DCFCE7' : '#FEF3C7',
                  color: locationState === 'live' ? '#166534' : '#92400E',
                }}
              />
            </Stack>

            <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
              <Metric
                label="Shift"
                grow={1.6}
                value={`${moment(duty.start).format('HH:mm')}-${moment(
                  duty.end,
                ).format('HH:mm')}`}
                icon={<AccessTime fontSize="small" />}
              />
              <Metric
                label="Open"
                value={openJourneys.length}
                icon={<Route fontSize="small" />}
              />
              <Metric
                label="Active"
                value={activeJourneysCount}
                icon={<DirectionsCar fontSize="small" />}
              />
              <Metric
                label="Progress"
                value={`${progress}%`}
                icon={<ShieldOutlined fontSize="small" />}
              />
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: '#E2E8F0',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#0F766E',
                  borderRadius: 1,
                },
              }}
            />
          </Stack>
        </Paper>
      </CrewTopBar>

      <Stack spacing={1.5} sx={{ p: 1.5 }}>
        {error && <Alert severity="error">{error}</Alert>}

        {crewJobs?.enabled && (
          <Paper
            elevation={0}
            sx={{
              p: 1.25,
              borderRadius: 1,
              border: '1px solid #99F6E4',
              bgcolor: '#F0FDFA',
            }}
          >
            <Stack spacing={0.75}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<AddCircleOutlineRounded />}
                disabled={!crewJobs.canAdd}
                onClick={() => setJobDialog({ mode: 'create' })}
                sx={{
                  borderRadius: 1,
                  py: 1.15,
                  fontWeight: 950,
                  textTransform: 'none',
                  bgcolor: '#0F766E',
                  '&:hover': { bgcolor: '#115E59' },
                }}
              >
                Add a job
              </Button>
              <Typography sx={{ color: '#475569', fontSize: '0.8rem' }}>
                {crewJobs.canAdd
                  ? `Asked to move a patient by ${duty?.organisation?.orgName || 'the hospital'}? Add the job here — patient details can follow.`
                  : 'Clock in to this duty to add jobs.'}
              </Typography>
            </Stack>
          </Paper>
        )}

        {journey ? (
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: '1px solid #D8E3E0',
              bgcolor: '#fff',
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Box minWidth={0}>
                  <Typography sx={{ color: '#0F766E', fontWeight: 900 }}>
                    Current journey
                  </Typography>
                  <Typography sx={{ fontSize: '1.35rem', fontWeight: 950 }}>
                    {getPatientName(journey)}
                  </Typography>
                  <Typography sx={{ color: '#64748B', fontSize: '0.86rem' }}>
                    {journey?.booking?.bookingRef || journey?.journeyRef || ''}
                  </Typography>
                </Box>
                <Chip
                  label={workflow.stateLabel}
                  sx={{
                    alignSelf: 'flex-start',
                    borderRadius: 1,
                    bgcolor: '#E0F2FE',
                    color: '#075985',
                    fontWeight: 900,
                  }}
                />
              </Stack>

              <Divider />

              <StopRow
                label={
                  isRiskAssessmentJourney(journey) ? 'Visit Location' : 'Pickup'
                }
                location={journey.pickup}
                time={getJourneyTime(journey)}
                accent="#0F766E"
              />
              {!isRiskAssessmentJourney(journey) && (
                <StopRow
                  label="Dropoff"
                  location={journey.dropoff}
                  time={journey?.dropoff?.when?.targetArrivalTime}
                  accent="#2563EB"
                />
              )}

              <JourneyTimesStrip journey={journey} />

              <Divider />

              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 1,
                    bgcolor: '#F1F5F9',
                    color: '#475569',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Person />
                </Box>
                <Box minWidth={0}>
                  <Typography sx={{ fontWeight: 900, color: '#0F172A' }}>
                    {patient?.demographics?.dob
                      ? moment(patient.demographics.dob).format('DD/MM/YYYY')
                      : 'DOB unknown'}
                  </Typography>
                  <Typography sx={{ color: '#64748B', fontSize: '0.84rem' }}>
                    ID {patient?.patientID || 'Unknown'}
                  </Typography>
                </Box>
                <Chip
                  icon={<WheelchairPickup />}
                  label={patient?.mobility?.category || 'PTS'}
                  sx={{
                    ml: 'auto',
                    borderRadius: 1,
                    bgcolor: '#F8FAFC',
                    fontWeight: 800,
                  }}
                />
              </Stack>

              {getRiskFlags(journey).length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {getRiskFlags(journey).map((flag) => (
                    <Chip
                      key={flag}
                      icon={<WarningAmberRounded />}
                      label={flag}
                      color="error"
                      size="small"
                      sx={{ fontWeight: 800, borderRadius: 1 }}
                    />
                  ))}
                </Stack>
              )}

              {[
                ['Booking notes', journey?.booking?.bookingNotes],
                ['Patient notes', patient?.clinicalNotes],
                [
                  isRiskAssessmentJourney(journey)
                    ? 'Visit note'
                    : 'Pickup note',
                  journey?.pickup?.note,
                ],
                [
                  'Dropoff note',
                  !isRiskAssessmentJourney(journey) && journey?.dropoff?.note,
                ],
              ]
                .filter(([, text]) => text)
                .map(([label, text]) => (
                  <Alert
                    key={label}
                    severity={label === 'Booking notes' ? 'warning' : 'info'}
                    sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                  >
                    <strong>{label}:</strong> {text}
                  </Alert>
                ))}

              {journey.crewJob?.editable &&
                (jobOutstanding.length > 0 ? (
                  <Alert
                    severity="warning"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() =>
                          setJobDialog({ mode: 'edit', journeyID: journey._id })
                        }
                        sx={{ fontWeight: 900, whiteSpace: 'nowrap' }}
                      >
                        Add details
                      </Button>
                    }
                  >
                    Still needed to complete this job:{' '}
                    {describeOutstanding(jobOutstanding)}.
                  </Alert>
                ) : (
                  <Button
                    size="small"
                    color="inherit"
                    startIcon={<EditNoteRounded fontSize="small" />}
                    onClick={() =>
                      setJobDialog({ mode: 'edit', journeyID: journey._id })
                    }
                    sx={{
                      alignSelf: 'flex-start',
                      textTransform: 'none',
                      fontWeight: 800,
                      color: '#0F766E',
                    }}
                  >
                    Edit job details
                  </Button>
                ))}

              <Button
                fullWidth
                variant="outlined"
                startIcon={<AssignmentIndOutlined />}
                onClick={() => setDetailJourneyID(journey._id)}
                sx={{
                  borderRadius: 1,
                  fontWeight: 900,
                  textTransform: 'none',
                  borderColor: '#99F6E4',
                  color: '#0F766E',
                  bgcolor: '#F0FDFA',
                }}
              >
                Patient details, history &amp; risk assessments
              </Button>

              {isRiskAssessmentJourney(journey) && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    borderColor: '#99F6E4',
                    bgcolor: '#F0FDFA',
                  }}
                >
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography sx={{ fontWeight: 950 }}>
                        Home Risk Assessment
                      </Typography>
                      {latestAssessment && (
                        <RiskChip risk={latestAssessment.overallRisk} />
                      )}
                    </Stack>
                    <Typography sx={{ color: '#475569', fontSize: '0.85rem' }}>
                      This visit is a home risk assessment - complete it on
                      scene before closing the visit.
                    </Typography>
                    <Button
                      variant={crewAssessment ? 'outlined' : 'contained'}
                      color="primary"
                      startIcon={<ShieldOutlined />}
                      onClick={() => setRiskAssessmentOpen(true)}
                      sx={{
                        alignSelf: 'flex-start',
                        borderRadius: 1,
                        fontWeight: 900,
                        textTransform: 'none',
                      }}
                    >
                      {crewAssessment
                        ? 'Edit Risk Assessment'
                        : 'Complete Risk Assessment'}
                    </Button>
                  </Stack>
                </Paper>
              )}

              <Button
                size="small"
                color="inherit"
                startIcon={<ReportProblemOutlined fontSize="small" />}
                onClick={() => setDelayDialogOpen(true)}
                sx={{
                  alignSelf: 'flex-start',
                  textTransform: 'none',
                  fontWeight: 800,
                  color: '#92400E',
                }}
              >
                Report a delay
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Alert severity="success">No open journeys on this duty.</Alert>
        )}

        {nextJourney && (
          <Paper
            elevation={0}
            onClick={() => setDetailJourneyID(nextJourney._id)}
            sx={{
              p: 1.25,
              borderRadius: 1,
              border: '1px solid #D8E3E0',
              bgcolor: '#fff',
              cursor: 'pointer',
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: '#F1F5F9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#0F766E',
                }}
              >
                <Navigation fontSize="small" />
              </Box>
              <Box flex={1} minWidth={0}>
                <Typography
                  sx={{
                    color: '#64748B',
                    fontSize: '0.72rem',
                    fontWeight: 900,
                  }}
                >
                  NEXT
                </Typography>
                <Typography noWrap sx={{ fontWeight: 900 }}>
                  {getPatientName(nextJourney)}
                </Typography>
                <Typography
                  noWrap
                  sx={{ color: '#64748B', fontSize: '0.82rem' }}
                >
                  {getAddressLine(nextJourney.pickup)}
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 900 }}>
                {formatTime(getJourneyTime(nextJourney))}
              </Typography>
              <ChevronRight fontSize="small" sx={{ color: '#94A3B8' }} />
            </Stack>
          </Paper>
        )}

        {dashboard?.journeys?.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 1,
              border: '1px solid #D8E3E0',
              bgcolor: '#fff',
              overflow: 'hidden',
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              onClick={() => setJourneyListOpen((prev) => !prev)}
              sx={{ p: 1.25, cursor: 'pointer' }}
            >
              <Typography sx={{ fontWeight: 900, color: '#0F172A' }}>
                All journeys today ({dashboard.journeys.length})
              </Typography>
              {journeyListOpen ? <ExpandLess /> : <ExpandMore />}
            </Stack>
            {journeyListOpen && (
              <Stack divider={<Divider />}>
                {dashboard.journeys.map((j) => {
                  const tone = getStatusTone(j.status)
                  const isCurrent = String(j._id) === String(journey?._id)
                  return (
                    <Stack
                      key={j._id}
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                      role="button"
                      tabIndex={0}
                      onClick={() => setDetailJourneyID(j._id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          setDetailJourneyID(j._id)
                        }
                      }}
                      sx={{
                        px: 1.25,
                        py: 1,
                        cursor: 'pointer',
                        bgcolor: isCurrent ? '#F0FDFA' : 'transparent',
                        '&:hover': { bgcolor: '#F1F5F9' },
                      }}
                    >
                      <Typography sx={{ fontWeight: 900, minWidth: 42 }}>
                        {formatTime(getJourneyTime(j))}
                      </Typography>
                      <Box flex={1} minWidth={0}>
                        <Typography noWrap sx={{ fontWeight: 800 }}>
                          {getPatientName(j)}
                        </Typography>
                        <Typography
                          noWrap
                          sx={{ color: '#64748B', fontSize: '0.78rem' }}
                        >
                          {isRiskAssessmentJourney(j)
                            ? getAddressLine(j.pickup)
                            : `${j.pickup?.name || j.pickup?.town || j.pickup?.postCode || 'Pickup'} → ${j.dropoff?.name || j.dropoff?.town || j.dropoff?.postCode || 'Dropoff'}`}
                        </Typography>
                        {j.routeSize > 1 && (
                          <Typography
                            sx={{
                              color: '#0F766E',
                              fontSize: '0.72rem',
                              fontWeight: 900,
                            }}
                          >
                            Shared run · {j.routeSize} patients
                          </Typography>
                        )}
                        {j.crewJob?.editable &&
                          j.crewJob.outstanding?.length > 0 && (
                            <Typography
                              sx={{
                                color: '#92400E',
                                fontSize: '0.72rem',
                                fontWeight: 900,
                              }}
                            >
                              Details needed
                            </Typography>
                          )}
                      </Box>
                      <Chip
                        label={j.status}
                        size="small"
                        sx={{
                          bgcolor: tone.bg,
                          color: tone.fg,
                          fontWeight: 800,
                        }}
                      />
                      <ChevronRight fontSize="small" sx={{ color: '#94A3B8' }} />
                    </Stack>
                  )
                })}
              </Stack>
            )}
          </Paper>
        )}

        <CrewDutiesList />
      </Stack>

      {journey && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            p: 1.5,
            bgcolor: 'rgba(238,244,243,0.94)',
            backdropFilter: 'blur(12px)',
            borderTop: '1px solid #D8E3E0',
            zIndex: 20,
          }}
        >
          <Stack spacing={0.75}>
            <Typography
              sx={{ color: '#475569', fontSize: '0.78rem', fontWeight: 800 }}
            >
              {workflow.helperText}
            </Typography>
            {runCompanions.length > 0 && workflow.nextStatus && (
              <FormControlLabel
                sx={{ mx: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={stepWithRun}
                    onChange={(e) => setStepWithRun(e.target.checked)}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 800 }}>
                    Same time for{' '}
                    {runCompanions.map((j) => getPatientName(j)).join(', ')}
                  </Typography>
                }
              />
            )}
            <Button
              fullWidth
              variant="contained"
              color="success"
              size="large"
              startIcon={
                workflowActionLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CheckCircle />
                )
              }
              sx={{
                borderRadius: 1,
                py: 1.35,
                fontWeight: 950,
                textTransform: 'none',
              }}
              onClick={() => updateJourneyStatus(workflow.nextStatus)}
              disabled={!workflow.nextStatus || !!workflowActionLoading}
            >
              {workflowButtonLabel}
            </Button>
            {navigationTarget && (
              <>
                <Button
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  size="large"
                  startIcon={<Navigation />}
                  sx={{
                    borderRadius: 1,
                    py: 1.15,
                    fontWeight: 950,
                    textTransform: 'none',
                    bgcolor: '#fff',
                    borderColor: '#CBD5E1',
                    color: '#0F172A',
                  }}
                  onClick={(event) => setSatNavAnchorEl(event.currentTarget)}
                >
                  Open sat nav to {navigationTarget.label}
                </Button>
                <Menu
                  anchorEl={satNavAnchorEl}
                  open={!!satNavAnchorEl}
                  onClose={() => setSatNavAnchorEl(null)}
                >
                  {navigationOptions.map((option) => (
                    <MenuItem
                      key={option.label}
                      onClick={() => {
                        setSatNavAnchorEl(null)
                        window.open(option.url, '_blank', 'noopener')
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Stack>
        </Box>
      )}
      <CrewJourneyDetails
        open={!!detailJourney}
        journey={detailJourney}
        onClose={() => setDetailJourneyID(null)}
        onEditJob={(journeyID) => {
          setDetailJourneyID(null)
          setJobDialog({ mode: 'edit', journeyID })
        }}
      />
      <CrewJobDialog
        open={!!jobDialog && (jobDialog.mode === 'create' || !!jobDialogJourney)}
        mode={jobDialog?.mode}
        dutyID={duty?._id}
        journey={jobDialogJourney}
        notice={jobDialog?.notice}
        onClose={() => setJobDialog(null)}
        onSaved={async () => {
          const created = jobDialog?.mode === 'create'
          setJobDialog(null)
          setError(null)
          // The crew added this one themselves — no "new journey" alert
          if (created) previousJourneysRef.current = null
          await loadDashboard(true)
        }}
      />
      <RiskAssessmentDialog
        open={riskAssessmentOpen}
        onClose={() => setRiskAssessmentOpen(false)}
        onSave={saveJourneyAssessment}
        patient={patient}
        selectedAssessment={crewAssessment}
        saving={savingAssessment}
        defaultAssessmentType="Home Visit"
      />
      <PTSDutyChat
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        dutyID={duty?._id}
        title={`${duty?.vehicle?.callSign || 'PTS'} chat`}
        onMessageSent={() => loadDashboard(true)}
      />
      <ArrivalProximityDialog
        open={!!arrivalPrompt}
        check={arrivalPrompt?.check}
        actorLabel="You"
        onCancel={() => setArrivalPrompt(null)}
        onConfirm={async () => {
          const { status, locationSnapshot } = arrivalPrompt || {}
          setArrivalPrompt(null)
          await saveJourneyStatus(status, true, locationSnapshot)
        }}
        confirming={workflowActionLoading === 'updating'}
      />

      <Dialog
        open={delayDialogOpen}
        onClose={() => (delaySubmitting ? null : setDelayDialogOpen(false))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Report a delay</DialogTitle>
        <DialogContent>
          <TextField
            select
            autoFocus
            fullWidth
            label="Reason"
            value={delayReasonCode}
            onChange={(e) => setDelayReasonCode(e.target.value)}
            sx={{ mt: 1 }}
          >
            {DELAY_REASON_CODES.map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Additional detail (optional)"
            placeholder="e.g. Held up in traffic on the A38, expect ~15 min delay"
            value={delayReasonDraft}
            onChange={(e) => setDelayReasonDraft(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDelayDialogOpen(false)}
            disabled={delaySubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitDelayReason}
            disabled={delaySubmitting || !delayReasonCode}
          >
            {delaySubmitting ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {notices.slice(0, 1).map((notice) => (
        <Snackbar
          key={notice.id}
          open
          autoHideDuration={8000}
          onClose={() => dismissNotice(notice.id)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity="warning"
            variant="filled"
            onClose={() => dismissNotice(notice.id)}
            sx={{ fontWeight: 700 }}
          >
            {notice.text}
          </Alert>
        </Snackbar>
      ))}
    </Box>
  )
}

export default PTSCrewView
